import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, usersService } from '../services/api';
import UploadProgress from '../components/UploadProgress';
import { sanitizePhone } from '../utils/phone';
import { formatPersonName } from '../utils/personName';
import { formatDni, normalizeDni } from '../utils/dni';
import ConfirmPopup from '../components/ConfirmPopup';
import './Usuarios.css';

const PER_PAGE = 30;

const Usuarios = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRolesRows, setExpandedRolesRows] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [availableGroups, setAvailableGroups] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    dni: '',
    telefono: '',
    groups: [],
    is_active: true,
  });

  useEffect(() => {
    // Verificar autenticación
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    loadUsers();
    loadGroups();
  }, [navigate]);

  const loadGroups = async () => {
    try {
      const data = await authService.getAllGroups();
      setAvailableGroups(data.groups || []);
    } catch (err) {
      console.error('Error al cargar grupos:', err);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await usersService.getUsers();
      setUsers(data);
    } catch (err) {
      setError('Error al cargar usuarios');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox'
      ? checked
      : name === 'telefono'
        ? sanitizePhone(value)
        : name === 'dni'
          ? normalizeDni(value)
          : value;
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  const handleGroupsChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({
      ...prev,
      groups: selectedOptions
    }));
  };

  const toggleRolesRow = (userId) => {
    setExpandedRolesRows((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username || '',
        email: user.email || '',
        first_name: formatPersonName(user.first_name || ''),
        last_name: formatPersonName(user.last_name || ''),
        dni: normalizeDni(user.dni || ''),
        telefono: sanitizePhone(user.telefono || ''),
        groups: user.groups || [],
        is_active: user.is_active !== undefined ? user.is_active : true,
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        dni: '',
        telefono: '',
        groups: [], // Al crear, se asignará "Estudiantes" en el backend
        is_active: true,
      });
    }
    setShowModal(true);
    setError('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      dni: '',
      telefono: '',
      groups: [],
      is_active: true,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Preparar datos para enviar
      const submitData = { ...formData };
      
      // Eliminar username ya que el backend lo genera automáticamente al crear
      delete submitData.username;
      
      // Asegurar que groups sea un array válido
      if (!submitData.groups || !Array.isArray(submitData.groups)) {
        submitData.groups = [];
      }
      
      // Limpiar campos vacíos - convertir strings vacíos a null para campos opcionales
      if (submitData.telefono === '' || submitData.telefono == null) {
        submitData.telefono = null;
      } else {
        submitData.telefono = sanitizePhone(submitData.telefono);
      }
      if (submitData.dni) {
        submitData.dni = normalizeDni(submitData.dni);
      }
      
      // Asegurar que is_active sea un booleano
      if (typeof submitData.is_active !== 'boolean') {
        submitData.is_active = submitData.is_active === true || submitData.is_active === 'true';
      }
      
      // Validar campos requeridos antes de enviar
      if (!editingUser) {
        if (!submitData.email || !submitData.email.trim()) {
          setError('El email es requerido');
          setLoading(false);
          return;
        }
        if (!submitData.first_name || !submitData.first_name.trim()) {
          setError('El nombre es requerido');
          setLoading(false);
          return;
        }
        if (!submitData.last_name || !submitData.last_name.trim()) {
          setError('El apellido es requerido');
          setLoading(false);
          return;
        }
        if (!submitData.dni || !submitData.dni.trim()) {
          setError('El DNI es requerido (se usará como contraseña por defecto)');
          setLoading(false);
          return;
        }
      }

      console.log('Datos a enviar:', JSON.stringify(submitData, null, 2));

      if (editingUser) {
        await usersService.updateUser(editingUser.id, submitData);
      } else {
        await usersService.createUser(submitData);
      }
      handleCloseModal();
      loadUsers();
    } catch (err) {
      console.error('Error completo:', err);
      console.error('Error response:', err.response);
      const errorData = err.response?.data || {};
      console.error('Error data:', errorData);
      
      // Extraer mensaje de error de manera más completa
      let errorMessage = 'Error al guardar el usuario';
      
      // Mostrar todos los errores si hay múltiples
      const errors = [];
      
      if (errorData.error) {
        errors.push(errorData.error);
      }
      
      // Recorrer todos los campos con errores
      Object.keys(errorData).forEach(key => {
        if (Array.isArray(errorData[key]) && errorData[key].length > 0) {
          errors.push(`${key}: ${errorData[key][0]}`);
        } else if (typeof errorData[key] === 'string') {
          errors.push(`${key}: ${errorData[key]}`);
        }
      });
      
      if (errors.length > 0) {
        errorMessage = errors.join(' | ');
      } else {
        // Intentar obtener el primer error de cualquier campo
        const firstError = Object.values(errorData).flat()[0];
        if (firstError) {
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        }
      }
      
      console.error('Mensaje de error final:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const extension = file.name.split('.').pop().toLowerCase();
      if (['csv', 'xlsx', 'xls'].includes(extension)) {
        setUploadFile(file);
        setError('');
      } else {
        setError('El archivo debe ser CSV o XLSX');
        setUploadFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      setError('Por favor selecciona un archivo');
      return;
    }

    try {
      setError('');
      setLoading(true);
      setUploadProgress(0);
      const result = await usersService.bulkUploadUsers(uploadFile, {
        onUploadProgress: (percent) => setUploadProgress(percent),
      });
      setUploadResult(result);
      setUploadFile(null);
      setUploadProgress(100);
      loadUsers();
    } catch (err) {
      let errorMessage = 'Error al cargar el archivo';
      const data = err.response?.data;
      if (data) {
        if (typeof data === 'string') errorMessage = data;
        else if (data.error) errorMessage = data.error;
        else if (data.detail) errorMessage = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        else if (typeof data === 'object' && Object.keys(data).length > 0) {
          errorMessage = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`).join(' | ');
        }
      } else if (err.message) errorMessage = err.message;
      setError(errorMessage);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const filterLower = (filterText || '').trim().toLowerCase();
  const filteredUsers = filterLower
    ? users.filter(u => {
        const dni = normalizeDni(u.dni || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        const filterNorm = normalizeDni(filterLower).toLowerCase() || filterLower;
        return dni.includes(filterNorm) || email.includes(filterLower) || formatDni(u.dni).toLowerCase().includes(filterLower);
      })
    : users;

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PER_PAGE));
  const page = Math.min(currentPage, totalPages);
  const start = (page - 1) * PER_PAGE;
  const paginatedUsers = filteredUsers.slice(start, start + PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText]);

  if (loading && users.length === 0) {
    return (
      <div className="usuarios-loading">
        <div className="loading-spinner"></div>
        <p>Cargando usuarios...</p>
      </div>
    );
  }

  return (
    <div className="usuarios-container">
      <div className="usuarios-header">
        <h1>Gestión de Usuarios</h1>
        <div className="usuarios-actions">
          <button
            className="usuarios-btn usuarios-btn-upload"
            onClick={() => setShowUploadModal(true)}
          >
            📤 Carga Masiva
          </button>
          <button
            className="usuarios-btn usuarios-btn-primary"
            onClick={() => handleOpenModal()}
          >
            ➕ Nuevo Usuario
          </button>
        </div>
      </div>

      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />

      <div className="usuarios-filtro">
        <label htmlFor="usuarios-filtro-input" className="usuarios-filtro-label">
          Filtrar por DNI o email
        </label>
        <input
          id="usuarios-filtro-input"
          type="text"
          className="usuarios-filtro-input"
          placeholder="Escriba DNI o email..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </div>

      <div className="usuarios-table-container">
        <table className="usuarios-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Nombre</th>
              <th>Apellido</th>
              <th>DNI</th>
              <th>Teléfono</th>
              <th>Roles</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan="8" className="usuarios-empty">
                  {filterLower ? 'Ningún usuario coincide con el filtro' : 'No hay usuarios registrados'}
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.email || '-'}</td>
                  <td>{formatPersonName(user.first_name) || '-'}</td>
                  <td>{formatPersonName(user.last_name) || '-'}</td>
                  <td>{user.dni ? formatDni(user.dni) : '-'}</td>
                  <td>{user.telefono != null && user.telefono !== '' ? sanitizePhone(user.telefono) || '-' : '-'}</td>
                  <td>
                    {user.groups && user.groups.length > 0 ? (
                      <div className="usuarios-groups">
                        {(expandedRolesRows[user.id] ? user.groups : [user.groups[0]]).map((group, index) => (
                          <span key={index} className="usuarios-group-badge">
                            {group}
                          </span>
                        ))}
                        {user.groups.length > 1 && (
                          <button
                            type="button"
                            className="usuarios-roles-ver-mas"
                            onClick={() => toggleRolesRow(user.id)}
                          >
                            {expandedRolesRows[user.id] ? 'Ver menos' : `Ver más (${user.groups.length - 1})`}
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="usuarios-group-badge">Estudiantes</span>
                    )}
                  </td>
                  <td>
                    <span className={`usuarios-status ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="usuarios-col-acciones">
                    <div className="usuarios-actions-cell">
                      <button
                        className="usuarios-btn-action usuarios-btn-edit"
                        onClick={() => handleOpenModal(user)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredUsers.length > 0 && (
        <div className="usuarios-paginacion">
          <button
            type="button"
            className="usuarios-btn usuarios-btn-paginacion"
            disabled={page <= 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          >
            Anterior
          </button>
          <span className="usuarios-paginacion-info">
            Mostrando {start + 1}-{Math.min(start + PER_PAGE, filteredUsers.length)} de {filteredUsers.length}
            {totalPages > 1 && ` · Página ${page} de ${totalPages}`}
          </span>
          <button
            type="button"
            className="usuarios-btn usuarios-btn-paginacion"
            disabled={page >= totalPages}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Modal de Crear/Editar */}
      {showModal && (
        <div className="usuarios-modal-overlay">
          <div className="usuarios-modal">
            <div className="usuarios-modal-header">
              <h2>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
              <button className="usuarios-modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} className="usuarios-form">
              <div className="usuarios-form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="usuarios-form-row">
                <div className="usuarios-form-group">
                  <label htmlFor="first_name">Nombre *</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="usuarios-form-group">
                  <label htmlFor="last_name">Apellido *</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="usuarios-form-row">
                <div className="usuarios-form-group">
                  <label htmlFor="dni">DNI * {!editingUser && '(se usará como contraseña por defecto)'}</label>
                  <input
                    type="text"
                    id="dni"
                    name="dni"
                    value={formData.dni}
                    onChange={handleInputChange}
                    required={!editingUser}
                    pattern="[A-Za-z0-9]{7,20}"
                    maxLength="20"
                    title="Solo letras y números, entre 7 y 20 caracteres"
                  />
                  {!editingUser && (
                    <small className="usuarios-form-help" style={{display: 'block', marginTop: '5px', fontSize: '0.85em', color: '#666'}}>
                      El DNI será la contraseña inicial del usuario.
                    </small>
                  )}
                </div>
                <div className="usuarios-form-group">
                  <label htmlFor="telefono">Teléfono</label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    inputMode="numeric"
                    maxLength={20}
                    pattern="\+?[0-9]*"
                    title="Solo números enteros; opcionalmente un + al inicio"
                  />
                </div>
              </div>
              {editingUser && (
                <div className="usuarios-form-group">
                  <label htmlFor="groups">Roles (puedes seleccionar múltiples)</label>
                  <select
                    id="groups"
                    name="groups"
                    multiple
                    value={formData.groups || []}
                    onChange={handleGroupsChange}
                    className="usuarios-select-multiple"
                    size={Math.min(availableGroups.length, 6)}
                  >
                    {availableGroups.map(group => (
                      <option key={group.id} value={group.name}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                  <small className="usuarios-form-help">
                    Mantén presionada la tecla Ctrl (o Cmd en Mac) para seleccionar múltiples roles
                  </small>
                  {formData.groups && formData.groups.length > 0 && (
                    <div className="usuarios-selected-groups">
                      <strong>Roles seleccionados:</strong>
                      <div className="usuarios-groups">
                        {formData.groups.map((groupName, idx) => (
                          <span key={idx} className="usuarios-group-badge">
                            {groupName}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="usuarios-form-group">
                <label className="usuarios-checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  <span>Usuario activo</span>
                </label>
              </div>
              <div className="usuarios-modal-actions">
                <button type="button" className="usuarios-btn usuarios-btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="usuarios-btn usuarios-btn-primary">
                  {editingUser ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Modal de Carga Masiva */}
      {showUploadModal && (
        <div className="usuarios-modal-overlay">
          <div className="usuarios-modal">
            <div className="usuarios-modal-header">
              <h2>Carga Masiva de Usuarios</h2>
              <button className="usuarios-modal-close" onClick={() => setShowUploadModal(false)}>
                ✕
              </button>
            </div>
            <div className="usuarios-modal-body">
              <p className="usuarios-upload-info">
                Sube un archivo CSV o XLSX con los datos del usuario (Se pueden cargar los mismos campos que el formulario de registro). Obligatorias: <strong>first_name</strong> (nombre), <strong>last_name</strong> (apellido), <strong>email</strong>, <strong>dni</strong>. El resto son opcionales (los campos que muchas opciones se seleccionan por ID: genero_id, pais_id, provincia_id, etc.). Incluye <strong>fecha_inscripcion</strong> (si está vacía se usa la fecha de carga) y <strong>curso</strong> (ID del curso para inscribir al usuario; si está vacío no se inscribe a ningún curso).
              </p>
              <div className="usuarios-upload-template">
                <span>Descargar archivo de ejemplo:</span>
                <a
                  href="/plantillas/carga_masiva_usuarios.csv"
                  download="carga_masiva_usuarios.csv"
                  className="usuarios-btn-link"
                >
                  CSV
                </a>
                <span> | </span>
                <a
                  href="/plantillas/carga_masiva_usuarios.xlsx"
                  download="carga_masiva_usuarios.xlsx"
                  className="usuarios-btn-link"
                >
                  XLSX
                </a>
              </div>
              <div className="usuarios-form-group">
                <label htmlFor="file-upload" className="usuarios-file-label">
                  Seleccionar archivo
                </label>
                <input
                  type="file"
                  id="file-upload"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="usuarios-file-input"
                />
                {uploadFile && (
                  <div className="usuarios-file-selected">
                    📄 {uploadFile.name}
                  </div>
                )}
              </div>
              {loading && (
                <div className="usuarios-upload-progress">
                  <UploadProgress
                    percent={uploadProgress}
                    label={uploadProgress >= 100 ? 'Listo para procesar' : `Subiendo… ${uploadProgress}%`}
                  />
                </div>
              )}
              {uploadResult && (
                <div className="usuarios-upload-result">
                  <h3>Resultado de la carga:</h3>
                  <div className="usuarios-upload-summary">
                    <p>✅ Creados: {uploadResult.summary?.created || 0}</p>
                    <p>🔄 Actualizados: {uploadResult.summary?.updated || 0}</p>
                    <p>❌ Errores: {uploadResult.summary?.errors || 0}</p>
                  </div>
                  {uploadResult.details?.errors?.length > 0 && (
                    <div className="usuarios-upload-errors-detail">
                      <strong>Detalle de errores por fila:</strong>
                      <ul>
                        {uploadResult.details.errors.map((item, idx) => (
                          <li key={idx}>Fila {item.row}: {item.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="usuarios-modal-actions">
              <button
                type="button"
                className="usuarios-btn usuarios-btn-secondary"
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setUploadResult(null);
                  setError('');
                }}
              >
                Cerrar
              </button>
              <button
                type="button"
                className="usuarios-btn usuarios-btn-primary"
                onClick={handleUpload}
                disabled={!uploadFile || loading}
              >
                {loading ? (uploadProgress >= 100 ? 'Procesando...' : 'Subiendo...') : 'Cargar Archivo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios;
